import { atom } from '@reatom/framework'
import * as DateF from 'date-fns'

import { AsyncStateN, Duration, pipe, Result } from '@blade/std-lib'
import { CurrencyModel } from '@root/entities/currency'
import { renderAvatarOrText } from '@root/features/trade/position/pnl-card/ui/canvas-utils'
import { DATE_FORMAT, DomUtil, Error } from '@root/shared'
import { NumericFormat } from '@root/shared/core/format'

import PNL_BG_PNG from '../assets/pnl-bg.webp'

import type { Ctx } from '@reatom/framework'
import type { BestDeal } from '@root/entities/profile'
import type { UserShort } from '@root/entities/user-short'
import type BigNumber from 'bignumber.js'

type TransKeys =
  | 'common.pnl'
  | 'common.position'
  | 'common.total-profit'
  | 'common.entry-price'
  | 'common.closed-price'
  | 'common.total-invested'
  | 'common.total-sold'

// TODO: Ivan - move shared methods to CanvasPnlBuilderTemplate
export class CanvasPnlBuilder {
  private position: BestDeal.BestDeal
  private user: UserShort.UserShort
  private translations: Record<TransKeys, string>

  public canvas!: HTMLCanvasElement
  public state = atom(
    AsyncStateN.notInitiated<Error.UnknownError, undefined>(),
    'canvas-pnl-builder-state',
  )
  private ctx!: CanvasRenderingContext2D
  private reatomCtx: Ctx

  // Background image width
  private BG_WIDTH = 369 * 2
  private BG_RATIO = 1.61
  private IMG_TIMEOUT = Duration.minutes(1)

  /**
   * @param {HTMLCanvasElement} canvas - The canvas element
   * @param {TradingPosition.TradingPosition} position - The trading position
   */
  constructor(
    position: BestDeal.BestDeal,
    user: UserShort.UserShort,
    translations: Record<string, string>,
    reatomCtx: Ctx,
  ) {
    this.position = position
    this.user = user
    this.translations = translations
    this.reatomCtx = reatomCtx
  }

  public setCanvas(canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d')

    if (!ctx) {
      this.state(
        this.reatomCtx,
        AsyncStateN.error(
          Error.UnknownError.fromUnknown('Canvas context not found'),
        ),
      )
      return this
    }

    this.ctx = ctx
    this.canvas = canvas

    return this
  }

  public async build() {
    this.canvas.width = this.BG_WIDTH
    this.canvas.height = this.BG_WIDTH * this.BG_RATIO

    this.state(this.reatomCtx, AsyncStateN.loading())

    await this.connectFonts()

    const imgResult = await DomUtil.loadImage(PNL_BG_PNG, this.IMG_TIMEOUT)

    if (Result.isSuccess(imgResult)) {
      this.ctx.drawImage(
        imgResult.success,
        0,
        0,
        imgResult.success.width,
        imgResult.success.height,
        0,
        0,
        this.canvas.width,
        this.canvas.height,
      )

      this.renderPnl()
      this.renderCurrency()
      this.renderPosition()
      this.renderTotalProfit()
      this.renderDate()
      this.renderUser()
    }

    this.state(
      this.reatomCtx,
      AsyncStateN.fromResult(
        pipe(
          imgResult,
          Result.map(() => undefined),
          Result.mapError(() =>
            Error.UnknownError.fromUnknown('Failed to load image'),
          ),
        ),
      ),
    )
  }

  public toDataUrl = () => {
    return this.canvas.toDataURL()
  }
  public toBlob = (): Promise<Blob | undefined> => {
    return new Promise((resolve) => {
      this.canvas.toBlob((blob) => {
        resolve(blob ?? undefined)
      })
    })
  }

  /**
   * @description renders the PNL
   */
  protected renderPnl = () => {
    this.ctx.save()
    try {
      // Title
      this.ctx.font = '32px Inter_SemiBold'
      this.ctx.fillStyle = '#fff'
      this.ctx.fillText(
        this.translations['common.pnl'],
        this.x(30),
        this.x(138),
      )

      // Percentage
      this.ctx.font = '72px Inter_Bold'
      this.ctx.fillStyle = '#50FF03'
      this.ctx.fillText(
        `${this.position.pnlPercents.toFixed(2)}%`,
        this.x(30),
        this.x(180),
      )

      const x = 30
      const y = 210

      const xx = this.renderPriceNative(
        this.position.pnlNative,
        x,
        y,
        28,
        '#fff',
      )

      this.ctx.font = '28px InterRegular'
      this.ctx.fillStyle = '#77777755'
      this.ctx.fillText(' | ', this.x(xx), this.x(y - 3))

      this.renderPriceFiat(this.position.pnlUsd, xx + 10, y, 28, '#fff')
    } finally {
      this.ctx.restore()
    }
  }

  /**
   * @description renders the currency Icon and ticker
   */
  protected renderCurrency = () => {
    const isLongTicker = this.position.currency.ticker.length > 10
    const ticker = isLongTicker
      ? this.position.currency.ticker.slice(0, 10)
      : this.position.currency.ticker
    const icon = this.reatomCtx.get(this.position.currency.icon)

    const ch = this.getCurrencySizePx(ticker)
    const r = 18
    const bg = (icon ? 50 : 50 - r * 2) + ch + 12
    const x = this.BG_WIDTH / 2 - bg - 25
    const y = 148

    this.ctx.save()
    try {
      // Background
      this.ctx.beginPath()
      this.ctx.roundRect(this.x(x - 3), this.x(y - 3), this.x(bg), this.x(42), [
        50,
      ])
      this.ctx.fillStyle = 'rgba(255, 255, 255, 0.07)'
      this.ctx.fill()

      // Icon if present
      if (icon) {
        this.image(x, y, r, icon)
      }

      // Ticker
      this.renderCurrencyTicker(x + (icon ? 44 : 10), y + 24)
    } finally {
      this.ctx.restore()
    }
  }

  /**
   * @description render currency ticker
   * - if ticker is long, it will be cropped to 10 characters with decreased opacity
   * - if ticker is short, it will be rendered as is
   */
  protected renderCurrencyTicker = (x: number, y: number) => {
    const isLongTicker = this.position.currency.ticker.length > 10

    this.ctx.save()
    try {
      this.ctx.font = '28px Inter_SemiBold'

      if (isLongTicker) {
        const ticker = this.position.currency.ticker.slice(0, 10)
        const end = this.x(x) + this.ctx.measureText(ticker).width
        const from = (end - this.x(10)) / this.canvas.width
        const to = end / this.canvas.width

        const gradient = this.ctx.createLinearGradient(
          0,
          0,
          this.canvas.width,
          0,
        )

        gradient.addColorStop(0, 'white')
        gradient.addColorStop(from, 'white')
        gradient.addColorStop(to, 'transparent')
        gradient.addColorStop(1, 'transparent')

        this.ctx.fillStyle = gradient

        // left for debugging
        // this.ctx.fillRect(0, 50, this.canvas.width, 100)

        this.ctx.fillText(
          this.position.currency.ticker.slice(0, 10),
          this.x(x),
          this.x(y),
        )
      } else {
        this.ctx.fillStyle = '#fff'
        this.ctx.fillText(this.position.currency.ticker, this.x(x), this.x(y))
      }
    } finally {
      this.ctx.restore()
    }
  }

  /**
   * @description render position, entry price, total invested, close price, total sold
   */
  protected renderPosition = () => {
    this.ctx.save()
    try {
      this.ctx.font = '32px Inter_SemiBold'
      this.ctx.fillStyle = '#fff'
      this.ctx.textAlign = 'center'
      this.ctx.fillText(
        this.translations['common.position'],
        this.BG_WIDTH / 2,
        this.x(257),
      )

      // Labels
      this.ctx.font = '24px Inter_Bold'
      this.ctx.fillStyle = '#6E6E6E'
      this.ctx.textAlign = 'left'
      this.ctx.fillText(
        this.translations['common.entry-price'],
        this.x(45),
        this.x(285),
      )
      this.ctx.fillText(
        this.translations['common.total-invested'],
        this.x(45),
        this.x(363),
      )
      this.ctx.fillText(
        this.translations['common.closed-price'],
        this.x(210),
        this.x(285),
      )
      this.ctx.fillText(
        this.translations['common.total-sold'],
        this.x(210),
        this.x(363),
      )

      // Native values
      this.renderPriceNative(
        this.position.entryPriceNative,
        45,
        313,
        32,
        '#fff',
      )
      this.renderPriceNative(
        this.position.closePriceNative,
        210,
        313,
        32,
        '#fff',
      )
      this.renderPriceNative(
        this.position.totalInvestedNative,
        45,
        389,
        32,
        '#fff',
      )
      this.renderPriceNative(
        this.position.totalSoldNative,
        210,
        389,
        32,
        '#fff',
      )

      // USD values
      this.renderPriceFiat(this.position.entryPriceUsd, 45, 329, 24, '#6E6E6E')
      this.renderPriceFiat(this.position.closePriceUsd, 210, 329, 24, '#6E6E6E')
      this.renderPriceFiat(
        this.position.totalInvestedUsd,
        45,
        406,
        24,
        '#6E6E6E',
      )
      this.renderPriceFiat(this.position.totalSoldUsd, 210, 406, 24, '#6E6E6E')
    } finally {
      this.ctx.restore()
    }
  }

  /**
   * @description
   * Renders the total profit
   */
  protected renderTotalProfit = () => {
    this.ctx.save()
    try {
      this.ctx.textAlign = 'center'
      this.ctx.fillStyle = '#50FF03'
      this.ctx.font = '24px Inter_Bold'
      this.ctx.fillText(
        this.translations['common.total-profit'],
        this.BG_WIDTH / 2,
        this.x(440),
      )

      this.renderPriceNativeCentered(
        this.position.totalProfitNative,
        466,
        40,
        '#50FF03',
      )

      this.renderPriceFiatCentered(
        this.position.totalProfitUsd,
        488,
        40,
        '#fff',
      )
    } finally {
      this.ctx.restore()
    }
  }

  /**
   * @description Render order date
   * - uses close date, if it's present
   * - otherwise the date when the position was created
   */
  protected renderDate = () => {
    const date = this.position.closeDate

    this.ctx.save()
    try {
      this.ctx.textAlign = 'right'
      this.ctx.fillStyle = 'rgba(255, 255, 255, 0.23)'
      this.ctx.font = '24px Inter_SemiBold'
      this.ctx.fillText(
        DateF.format(date, DATE_FORMAT.HH_mm),
        this.x(340),
        this.x(535),
      )

      this.ctx.fillStyle = '#FFF'
      this.ctx.font = '32px Inter_SemiBold'
      this.ctx.fillText(
        DateF.format(date, DATE_FORMAT.dd_MM_yy),
        this.x(340),
        this.x(555),
      )
    } finally {
      this.ctx.restore()
    }
  }

  /**
   * @description render the user photo(if it's present) with user name and last name
   */
  protected renderUser = () => {
    const initials =
      `${this.user.firstName ?? this.user.username}${this.user.lastName}`
        .slice(0, 2)
        .toUpperCase()

    this.ctx.save()
    try {
      renderAvatarOrText(
        this.ctx,
        32,
        518,
        25,
        '#ffffff33',
        initials,
        this.user.avatarSrc,
      )

      this.ctx.textAlign = 'left'
      this.ctx.fillStyle = '#FFF'
      this.ctx.font = '32px Inter_SemiBold'

      const xName = 88

      // If first name and last name are present
      if (this.user.lastName) {
        this.ctx.fillText(
          this.user.firstName ?? this.user.username,
          this.x(xName),
          this.x(540),
        )
        this.ctx.fillText(this.user.lastName, this.x(xName), this.x(558))
      } else {
        // Otherwise fallback to first name
        this.ctx.fillText(
          this.user.firstName ?? this.user.username,
          this.x(xName),
          this.x(548),
        )
      }
    } finally {
      this.ctx.restore()
    }
  }

  /**
   * @description Format and render price in native currency centered
   * @param {BigNumber | number} x - The value
   * @returns {string} The formatted value
   */
  private renderPriceNativeCentered = (
    value: BigNumber | number,
    y: number,
    size: number,
    fillStyle: string,
  ) => {
    const nativeCurrency = CurrencyModel.nativeOf(
      this.position.currency.network,
    )
    const parts = NumericFormat.formatPresetToParts(
      value,
      'currency-amount-short',
      {
        currency: nativeCurrency.ticker,
      },
    )

    this.renderPriceCentered(parts, y, size, fillStyle)
  }

  /**
   * @description Format and render price in USD centered
   * @param {BigNumber | number} x - The value
   * @returns {string} The formatted value
   */
  private renderPriceFiatCentered = (
    value: BigNumber | number,
    y: number,
    size: number,
    fillStyle: string,
  ) => {
    const parts = NumericFormat.formatPresetToParts(
      value,
      'currency-amount-short',
      {
        currency: '$',
      },
    )

    this.renderPriceCentered(parts, y, size, fillStyle)
  }

  /**
   * @description helper method to format price centered
   * @param {BigNumber | number} x - The value
   * @returns {string} The formatted value
   */
  private renderPriceCentered = (
    parts: Array<NumericFormat.FormattingPart>,
    y: number,
    size: number,
    fillStyle: string,
  ) => {
    const x = this.BG_WIDTH / 4
    this.ctx.save()
    try {
      this.ctx.textAlign = 'left'

      // Measure the width of the text with transparent color
      const width = this.renderPrice(
        parts,
        0,
        y,
        size,
        'rgba(255, 255, 255, 0)',
      )

      // Make the actual drawing
      this.ctx.fillStyle = fillStyle
      this.renderPrice(parts, x - width / 2, y, size, fillStyle)
    } finally {
      this.ctx.restore()
    }
  }

  /**
   * @description helper method to format and render price in USD
   * @param {BigNumber | number} x - The value
   * @returns {string} The formatted value
   */
  private renderPriceFiat = (
    value: BigNumber | number,
    x: number,
    y: number,
    size: number = 24,
    fillStyle: string = '#6E6E6E',
  ): number => {
    const parts = NumericFormat.formatPresetToParts(
      value,
      'currency-amount-short',
      {
        currency: '$',
      },
    )

    return this.renderPrice(parts, x, y, size, fillStyle)
  }

  /**
   * @description helper method to format and render price in native currency
   * @param {BigNumber | number} x - The value
   * @returns {string} The formatted value
   */
  private renderPriceNative = (
    value: BigNumber | number,
    x: number,
    y: number,
    size: number = 28,
    fillStyle: string = '#fff',
  ): number => {
    const nativeCurrency = CurrencyModel.nativeOf(
      this.position.currency.network,
    )
    const parts = NumericFormat.formatPresetToParts(
      value,
      'currency-amount-short',
      {
        currency: nativeCurrency.ticker,
      },
    )

    return this.renderPrice(parts, x, y, size, fillStyle)
  }

  /**
   * @description helper method to render the price
   * @param {string} text - The text
   * @param {number} x - The x coordinate
   * @param {number} y - The y coordinate
   * @returns {number} The width of the text
   */
  private renderPrice = (
    parts: Array<NumericFormat.FormattingPart>,
    x: number,
    y: number,
    size: number = 28,
    fillStyle: string = '#fff',
  ): number => {
    this.ctx.save()
    try {
      this.ctx.fillStyle = fillStyle

      let xx = x

      for (const part of Object.values(parts)) {
        if (part.type === 'compact-decimal-zeroes') {
          this.ctx.font = `${size / 1.8}px Inter_Medium`
          this.ctx.fillText(part.value, this.x(xx), this.x(y))
        } else {
          this.ctx.font = `${size}px Inter_Medium`
          this.ctx.fillText(part.value, this.x(xx), this.x(y - 4))
        }

        xx += this.ctx.measureText(part.value).width / 2
      }

      return xx
    } finally {
      this.ctx.restore()
    }
  }

  /**
   * @description helper method to get character width in pixels
   * @param {string} ticker - The ticker
   * @returns {number} The size of the ticker
   */
  private getCurrencySizePx = (ticker: string): number => {
    const cropped = ticker.length > 10 ? ticker.slice(0, 10) : ticker

    this.ctx.save()
    try {
      this.ctx.font = '14px Inter_SemiBold'

      return this.ctx.measureText(cropped).width
    } finally {
      this.ctx.restore()
    }
  }

  /**
   * @description helper method to draw an image with rounded corners on the canvas
   * @param {number} x - The x coordinate
   * @param {number} y - The y coordinate
   * @param {number} r - The radius
   * @param {string} src - The image source
   */
  private image = (x: number, y: number, r: number, src: string): void => {
    const img = new Image()
    img.src = src
    img.crossOrigin = 'Anonymous'
    img.onload = () => {
      this.ctx.save()
      try {
        this.ctx.beginPath()
        this.ctx.arc(
          this.x(x + r),
          this.x(y + r),
          this.x(r),
          0,
          Math.PI * 2,
          true,
        )
        this.ctx.closePath()
        this.ctx.clip()

        this.ctx.drawImage(
          img,
          this.x(x),
          this.x(y),
          this.x(r * 2),
          this.x(r * 2),
        )
      } finally {
        this.ctx.restore()
      }
    }
  }

  /**
   * @description helper method to scales the size by const
   * @param {number} x - The size
   * @returns {number} The size scaled by two
   */
  private x = (x: number): number => x * 2

  // TODO: Ivan - dry, use CanvasPnlBuilderTemplate.connectFonts
  /**
   * @description load Inter fonts to the canvas
   * @link https://developer.mozilla.org/en-US/docs/Web/API/FontFace
   */
  private connectFonts = async () => {
    const fonts = [
      new FontFace(
        'Inter_Regular',
        `url(/fonts/inter-cyrillic-latin-400.woff2) format('woff2')`,
        {
          style: 'normal',
          weight: '400',
          stretch: 'condensed',
        },
      ),
      new FontFace(
        'Inter_Medium',
        `url(/fonts/inter-cyrillic-latin-500.woff2) format('woff2')`,
        {
          style: 'normal',
          weight: '500',
          stretch: 'condensed',
        },
      ),
      new FontFace(
        'Inter_SemiBold',
        `url(/fonts/inter-cyrillic-latin-600.woff2) format('woff2')`,
        {
          style: 'normal',
          weight: '600',
          stretch: 'condensed',
        },
      ),
      new FontFace(
        'Inter_Bold',
        `url(/fonts/inter-cyrillic-latin-700.woff2) format('woff2')`,
        {
          style: 'normal',
          weight: '700',
          stretch: 'condensed',
        },
      ),
    ]

    await Promise.all(fonts.map((font) => font.load()))
    fonts.map((font) => document.fonts.add(font))
  }
}
